import { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, Capabilities } from '@/api/client'
import { usePageLoader } from '@/components/LogoLoader'
import { useMe } from '@/features/auth/useAuth'
import { fmtDate, fmtDateTime, fmtEur, fmtNumber, useT } from '@/i18n'
import { fleetMessages } from '@/i18n/messages/fleet'
import { Select } from '@/components/Select'
import { Dialog, Field, GhostButton, INPUT, PrimaryButton } from '@/features/inventory/shared'
import { FixNote, FuelGauge, TuvText, VehicleStatusBadge } from './shared'
import { fixOrNull, usePhoneFix } from './usePhoneFix'
import { fleetMapMessages } from '@/i18n/messages/fleetMap'
import { shrinkPhoto, todayIso, addDays, vehicleName, type CrewChoice, type ProjectChoice, type Reservation, type Trip, type VehicleDetail } from './types'

type Action = 'reserve' | 'checkout' | 'checkin'

/** One vehicle: what it is (AU's register), who has or has booked it, and every use. */
export function VehiclePage() {
  const t = useT(fleetMessages)
  const tm = useT(fleetMapMessages)
  const { id } = useParams<{ id: string }>()
  const { data: me } = useMe()
  const canMove = me?.capabilities.includes(Capabilities.MoveFleet) ?? false
  const [action, setAction] = useState<Action | null>(null)
  const qc = useQueryClient()
  const { data, isPending, error } = useQuery<VehicleDetail>({
    queryKey: ['fleet-vehicle', id], queryFn: () => api.get<VehicleDetail>(`/api/fleet/vehicles/${id}`), enabled: Boolean(id),
  })
  const cancel = useMutation({
    mutationFn: (rid: string) => api.post(`/api/fleet/reservations/${rid}/cancel`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fleet-vehicle', id] }),
  })
  const [confirming, setConfirming] = useState<string | null>(null)

  const loader = usePageLoader(isPending, t('loadingVehicle'))
  if (loader) return loader
  if (error || !data) return <p className="text-sm" style={{ color: 'var(--viz-critical)' }}>{t('notFound')}</p>

  const v = data.vehicle
  const upcoming = data.reservations.filter((r) => r.status === 'reserved' && r.endsOn >= todayIso())

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <div className="flex items-center justify-between gap-2">
        <Link to="/fleet/list" className="text-xs text-neutral-500 hover:underline">← {t('title')}</Link>
        <Link to={`/fleet?v=${v.id}`} className="text-xs font-medium text-brand-700 hover:underline dark:text-brand-300">📍 {tm('showOnMap')}</Link>
      </div>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-mono text-2xl font-bold">{v.plate}</h1>
            <VehicleStatusBadge status={v.status} />
          </div>
          <p className="mt-0.5 text-lg">{vehicleName(v) || '—'} <span className="text-sm text-neutral-500">{v.vehicleType}</span></p>
        </div>
      </header>

      <dl className="grid grid-cols-2 gap-x-6 gap-y-2 rounded-2xl bg-white p-4 text-sm ring-1 ring-neutral-200 sm:grid-cols-4 dark:bg-neutral-900 dark:ring-neutral-800">
        <Info label={t('colTuv')}><TuvText due={v.tuvDue} /></Info>
        <Info label={t('colFinancing')}>{v.financing === 'leasing' ? `${t('leasing')}${v.monthlyPayment ? ` · ${fmtEur(v.monthlyPayment)}` : ''}` : v.financing === 'paid' ? t('owned') : '—'}</Info>
        <Info label={t('odometer')}>{v.lastOdometer != null ? `${fmtNumber(v.lastOdometer)} km` : '—'}</Info>
        <Info label="VIN"><span className="font-mono text-xs">{v.vin ?? '—'}</span></Info>
      </dl>

      {v.out && (
        <div className="rounded-2xl bg-amber-50 p-4 text-sm ring-1 ring-amber-200 dark:bg-amber-500/10 dark:ring-amber-500/30">
          <div className="font-semibold">{t('outWith', { name: v.out.driverName })}</div>
          <div className="mt-0.5 text-amber-900/80 dark:text-amber-200/80">
            {[v.out.kolonaName, v.out.projectName].filter(Boolean).join(' · ')} · {t('since', { date: fmtDateTime(v.out.outAt) })} · {fmtNumber(v.out.outOdometer)} km
          </div>
        </div>
      )}

      {canMove && (
        <div className="grid gap-2 sm:grid-cols-2">
          {v.out
            ? <PrimaryButton className="min-h-14 text-base sm:col-span-2" onClick={() => setAction('checkin')}>{t('checkIn')}</PrimaryButton>
            : <PrimaryButton className="min-h-14 text-base" onClick={() => setAction('checkout')}>{t('checkOut')}</PrimaryButton>}
          <GhostButton className="min-h-14" onClick={() => setAction('reserve')}>{t('reserve')}</GhostButton>
        </div>
      )}

      <section className="space-y-2">
        <h2 className="text-sm font-semibold">{t('bookings')}</h2>
        {upcoming.length === 0 ? <p className="text-sm text-neutral-500">{t('noBookings')}</p> : (
          <ul className="space-y-2">
            {upcoming.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-white p-3 text-sm ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
                <div>
                  <div className="font-medium">{fmtDate(r.startsOn)} – {fmtDate(r.endsOn)}</div>
                  <div className="text-xs text-neutral-500">{[r.kolonaName, r.projectName, r.purpose].filter(Boolean).join(' · ')}</div>
                </div>
                {canMove && (confirming === r.id ? (
                  <span className="flex items-center gap-2">
                    <button type="button" onClick={() => cancel.mutate(r.id)} className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white">{t('cancelBooking')}</button>
                    <button type="button" onClick={() => setConfirming(null)} className="text-xs text-neutral-500">{t('keep')}</button>
                  </span>
                ) : <button type="button" onClick={() => setConfirming(r.id)} className="text-xs text-red-600 hover:underline">{t('cancelBooking')}</button>)}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold">{t('trips')}</h2>
        {data.trips.length === 0 ? <p className="text-sm text-neutral-500">{t('noTrips')}</p> : (
          <ol className="space-y-2">{data.trips.map((x) => <TripItem key={x.id} trip={x} />)}</ol>
        )}
      </section>

      {action === 'reserve' && <ReserveDialog vehicleId={v.id} onClose={() => setAction(null)} />}
      {action === 'checkout' && <CheckOutDialog detail={data} onClose={() => setAction(null)} />}
      {action === 'checkin' && v.out && <CheckInDialog vehicleId={v.id} trip={v.out} onClose={() => setAction(null)} />}
    </div>
  )
}

function Info({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="min-w-0"><dt className="text-xs text-neutral-500">{label}</dt><dd className="truncate">{children}</dd></div>
}

function TripItem({ trip: x }: { trip: Trip }) {
  const t = useT(fleetMessages)
  return (
    <li className="rounded-xl bg-white p-3 text-sm ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-medium">{x.driverName}{x.kolonaName && <span className="text-neutral-500"> · {x.kolonaName}</span>}</span>
        <span className="tabular text-xs">{x.km != null ? `${fmtNumber(x.km)} km` : t('stillOut')}</span>
      </div>
      <div className="mt-0.5 text-xs text-neutral-500">
        {fmtDateTime(x.outAt)}{x.inAt && ` → ${fmtDateTime(x.inAt)}`}{x.projectName && ` · ${x.projectName}`}
        {' · '}{fmtNumber(x.outOdometer)}{x.inOdometer != null && ` → ${fmtNumber(x.inOdometer)}`} km
      </div>
      {x.inDamage && <div className="mt-1 text-xs font-semibold text-red-700 dark:text-red-300">⚠ {t('damageReported')}{x.inNote ? `: ${x.inNote}` : ''}</div>}
      {[...x.outPhotos, ...x.inPhotos].length > 0 && (
        <div className="mt-2 flex gap-1.5 overflow-x-auto">
          {[...x.outPhotos, ...x.inPhotos].map((p) => (
            <a key={p} href={`/api/fleet/photos/${p}`} target="_blank" rel="noreferrer">
              <img src={`/api/fleet/photos/${p}`} alt="" loading="lazy" className="h-16 w-20 rounded-lg object-cover ring-1 ring-neutral-200 dark:ring-neutral-700" />
            </a>
          ))}
        </div>
      )}
    </li>
  )
}

function useChoices() {
  const projects = useQuery<ProjectChoice[]>({ queryKey: ['fleet-projects'], queryFn: () => api.get<ProjectChoice[]>('/api/fleet/projects'), staleTime: 10 * 60_000 })
  const crews = useQuery<CrewChoice[]>({ queryKey: ['fleet-crews'], queryFn: () => api.get<CrewChoice[]>('/api/fleet/crews'), staleTime: 10 * 60_000 })
  return { projects: projects.data ?? [], crews: crews.data ?? [] }
}

function useSave(vehicleId: string, onClose: () => void) {
  const qc = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const done = async () => {
    await Promise.all([qc.invalidateQueries({ queryKey: ['fleet-vehicle', vehicleId] }), qc.invalidateQueries({ queryKey: ['fleet-vehicles'] }),
      qc.invalidateQueries({ queryKey: ['fleet-board'] })])
    onClose()
  }
  return { error, setError, done, fail: (e: unknown) => setError(e instanceof ApiError ? e.message : String(e)) }
}

function ProjectCrew({ project, setProject, crew, setCrew }: { project: string; setProject: (v: string) => void; crew: string; setCrew: (v: string) => void }) {
  const t = useT(fleetMessages)
  const { projects, crews } = useChoices()
  return (
    <>
      <Field label={t('project')}>
        <Select value={project} onChange={setProject} empty={{ value: '', label: '—' }}
          options={projects.map((p) => ({ value: p.projectId, label: `${p.name ?? '—'}${p.codeInternal ? ` · ${p.codeInternal}` : ''}` }))} />
      </Field>
      <Field label={t('crew')}>
        <Select value={crew} onChange={setCrew} empty={{ value: '', label: '—' }}
          options={crews.map((c) => ({ value: c.kolonaId, label: c.name }))} />
      </Field>
    </>
  )
}

function ReserveDialog({ vehicleId, onClose }: { vehicleId: string; onClose: () => void }) {
  const t = useT(fleetMessages)
  const { crews } = useChoices()
  const s = useSave(vehicleId, onClose)
  const [from, setFrom] = useState(todayIso())
  const [to, setTo] = useState(addDays(todayIso(), 4))
  const [project, setProject] = useState('')
  const [crew, setCrew] = useState('')
  const [purpose, setPurpose] = useState('')
  const save = useMutation({
    mutationFn: () => api.post('/api/fleet/reservations', {
      vehicleId, projectId: project || null, kolonaId: crew || null, kolonaName: crews.find((c) => c.kolonaId === crew)?.name ?? null,
      startsOn: from, endsOn: to, purpose: purpose.trim() || null,
    }),
    onSuccess: s.done, onError: s.fail,
  })
  return (
    <Dialog title={t('reserve')} hint={t('reserveHint')} onClose={onClose}
      footer={<><GhostButton onClick={onClose}>{t('cancel')}</GhostButton>
        <PrimaryButton disabled={!(project || crew) || to < from || save.isPending} onClick={() => save.mutate()}>{t('save')}</PrimaryButton></>}>
      <div className="grid grid-cols-2 gap-2">
        <Field label={t('from')}><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={INPUT} /></Field>
        <Field label={t('to')}><input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} className={INPUT} /></Field>
      </div>
      <ProjectCrew project={project} setProject={setProject} crew={crew} setCrew={setCrew} />
      <Field label={t('purpose')}><input value={purpose} onChange={(e) => setPurpose(e.target.value)} maxLength={300} className={INPUT} /></Field>
      {s.error && <p className="text-sm" style={{ color: 'var(--viz-critical)' }}>{s.error}</p>}
    </Dialog>
  )
}

const SIDES = ['front', 'back', 'left', 'right'] as const

function Photos({ photos, setPhotos }: { photos: Record<string, string>; setPhotos: React.Dispatch<React.SetStateAction<Record<string, string>>> }) {
  const t = useT(fleetMessages)
  const label = { front: t('sideFront'), back: t('sideBack'), left: t('sideLeft'), right: t('sideRight') }
  return (
    <Field label={t('photos')}>
      <div className="grid grid-cols-4 gap-2">
        {SIDES.map((side) => (
          <label key={side} className="relative flex aspect-square cursor-pointer flex-col items-center justify-center overflow-hidden rounded-xl bg-neutral-100 text-center text-[11px] text-neutral-500 ring-1 ring-neutral-200 dark:bg-neutral-800 dark:ring-neutral-700">
            {photos[side] ? <img src={photos[side]} alt="" className="absolute inset-0 h-full w-full object-cover" /> : <><span className="text-xl">📷</span>{label[side]}</>}
            <input type="file" accept="image/*" capture="environment" className="sr-only"
              onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return; const data = await shrinkPhoto(f); setPhotos((prev) => ({ ...prev, [side]: data })) }} />
          </label>
        ))}
      </div>
    </Field>
  )
}

function CheckOutDialog({ detail, onClose }: { detail: VehicleDetail; onClose: () => void }) {
  const t = useT(fleetMessages)
  const v = detail.vehicle
  const { crews } = useChoices()
  const s = useSave(v.id, onClose)
  const res: Reservation | null = v.current
  const [driver, setDriver] = useState('')
  const [project, setProject] = useState(res?.projectId ?? '')
  const [crew, setCrew] = useState(res?.kolonaId ?? '')
  const [odometer, setOdometer] = useState(v.lastOdometer != null ? String(v.lastOdometer) : '')
  const [fuel, setFuel] = useState(8)
  const [note, setNote] = useState('')
  const [photos, setPhotos] = useState<Record<string, string>>({})
  const fix = usePhoneFix()
  const odo = Number(odometer)
  const save = useMutation({
    mutationFn: () => api.post(`/api/fleet/vehicles/${v.id}/checkout`, {
      reservationId: res?.id ?? null, projectId: project || null, kolonaId: crew || null,
      kolonaName: crews.find((c) => c.kolonaId === crew)?.name ?? null, driverName: driver.trim(), odometer: odo, fuel,
      note: note.trim() || null, photos: Object.entries(photos).map(([side, data]) => ({ side, data })), position: fixOrNull(fix),
    }),
    onSuccess: s.done, onError: s.fail,
  })
  return (
    <Dialog title={t('checkOut')} hint={res ? t('forBooking', { from: fmtDate(res.startsOn), to: fmtDate(res.endsOn) }) : v.plate} onClose={onClose}
      footer={<><GhostButton onClick={onClose}>{t('cancel')}</GhostButton>
        <PrimaryButton disabled={!driver.trim() || !odometer || !Number.isFinite(odo) || save.isPending} onClick={() => save.mutate()}>{t('checkOut')}</PrimaryButton></>}>
      <Field label={t('driver')}><input value={driver} onChange={(e) => setDriver(e.target.value)} maxLength={200} className={INPUT} /></Field>
      <ProjectCrew project={project} setProject={setProject} crew={crew} setCrew={setCrew} />
      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
        <Field label={t('odometer')}><div className="flex items-center gap-2"><input inputMode="numeric" value={odometer} onChange={(e) => setOdometer(e.target.value.replace(/\D/g, ''))} className={`${INPUT} text-lg`} /><span>km</span></div></Field>
        <Field label={t('fuel')}><FuelGauge value={fuel} onChange={setFuel} /></Field>
      </div>
      <Photos photos={photos} setPhotos={setPhotos} />
      <Field label={t('note')}><input value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} className={INPUT} /></Field>
      <FixNote fix={fix} />
      {s.error && <p className="text-sm" style={{ color: 'var(--viz-critical)' }}>{s.error}</p>}
    </Dialog>
  )
}

function CheckInDialog({ vehicleId, trip, onClose }: { vehicleId: string; trip: Trip; onClose: () => void }) {
  const t = useT(fleetMessages)
  const s = useSave(vehicleId, onClose)
  const [odometer, setOdometer] = useState('')
  const [fuel, setFuel] = useState(trip.outFuel)
  const [damage, setDamage] = useState(false)
  const [note, setNote] = useState('')
  const [photos, setPhotos] = useState<Record<string, string>>({})
  const fix = usePhoneFix()
  const odo = Number(odometer)
  const km = useMemo(() => (odometer && Number.isFinite(odo) ? odo - trip.outOdometer : null), [odometer, odo, trip.outOdometer])
  const save = useMutation({
    mutationFn: () => api.post(`/api/fleet/vehicles/${vehicleId}/checkin`, {
      odometer: odo, fuel, damage, note: note.trim() || null, photos: Object.entries(photos).map(([side, data]) => ({ side, data })),
      position: fixOrNull(fix),
    }),
    onSuccess: s.done, onError: s.fail,
  })
  return (
    <Dialog title={t('checkIn')} hint={t('outWith', { name: trip.driverName })} onClose={onClose}
      footer={<><GhostButton onClick={onClose}>{t('cancel')}</GhostButton>
        <PrimaryButton disabled={!odometer || (km != null && km < 0) || save.isPending} onClick={() => save.mutate()}>{t('checkIn')}</PrimaryButton></>}>
      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
        <Field label={t('odometer')} hint={km != null && km >= 0 ? t('kmDriven', { km: fmtNumber(km) }) : t('leftWith', { km: fmtNumber(trip.outOdometer) })}>
          <div className="flex items-center gap-2"><input inputMode="numeric" autoFocus value={odometer} onChange={(e) => setOdometer(e.target.value.replace(/\D/g, ''))} className={`${INPUT} text-lg`} /><span>km</span></div>
        </Field>
        <Field label={t('fuel')}><FuelGauge value={fuel} onChange={setFuel} /></Field>
      </div>
      <Photos photos={photos} setPhotos={setPhotos} />
      <label className="flex min-h-11 items-center gap-2 text-sm">
        <input type="checkbox" checked={damage} onChange={(e) => setDamage(e.target.checked)} className="size-5" /> {t('damageNew')}
      </label>
      <Field label={damage ? t('damageWhat') : t('note')}><input value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} className={INPUT} /></Field>
      <FixNote fix={fix} />
      {s.error && <p className="text-sm" style={{ color: 'var(--viz-critical)' }}>{s.error}</p>}
    </Dialog>
  )
}
